import type { ReactNode } from 'react';
import { Skeleton } from '@parrot-software/design-system';

/**
 * Single skeleton card that mirrors the layout of `PaymentMethodCard`.
 *
 * Reproduces the same outer label box (border, padding, shadow), the
 * icon + title header and the trailing radio, leaving the body to the caller so
 * each method (card / SPEI) can match its own content shape and sizes.
 *
 * @param props.children - Skeleton body matching the real card content
 * @returns React element rendering one placeholder card
 */
const PaymentMethodCardSkeleton = ({ children }: { children: ReactNode }) => (
  <div className="flex w-full items-start gap-3 rounded-xl border border-border bg-popover p-6 shadow-xs">
    <div className="flex min-w-0 flex-1 flex-col gap-3">
      <div className="flex items-center gap-2">
        <Skeleton className="h-6 w-6 rounded-md" />
        <Skeleton className="h-7 w-40" />
      </div>
      {children}
    </div>
    <Skeleton className="mt-1 h-4 w-4 shrink-0 rounded-full" />
  </div>
);

/**
 * Loading placeholder for the payment-method selection grid.
 *
 * Mirrors the same render logic as the real cards: the responsive grid
 * (single column on mobile, two columns from `md`), the card option (icon +
 * title + two body lines) and the SPEI option (icon + title + body lines +
 * bullet), so the UI does not jump when `useSubscriptionBlock` resolves.
 *
 * @returns React element rendering the two placeholder cards
 */
export const PaymentMethodCardsSkeleton = () => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
    <PaymentMethodCardSkeleton>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </PaymentMethodCardSkeleton>
    <PaymentMethodCardSkeleton>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
      <div className="ml-4 flex flex-col gap-2">
        <Skeleton className="h-3 w-3/4" />
      </div>
    </PaymentMethodCardSkeleton>
  </div>
);
